import axios from 'axios';
import { DEFAULT_LANGUAGE_KEY, LANGUAGE_KEY, OBJECT_KEY, REFRESH_TOKEN_KEY, TOKEN_KEY, USER_KEY } from '../Constants/MainKeys';
import AlertService from './alertService';

let isRefreshing = false;
let failedQueue = [];

const MAX_REFRESH_ATTEMPTS = 3;
const REFRESH_WINDOW_MS = 30 * 1000;
const AUTH_REFRESH_LOOP_KEY = "authRefreshLoopDetected";

let refreshAttemptCount = 0;
let refreshWindowStartedAt = null;

const processQueue = (error, token = null) => {
	failedQueue.forEach(prom => {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	});
	failedQueue = [];
};

const resetRefreshAttempts = () => {
	refreshAttemptCount = 0;
	refreshWindowStartedAt = null;
};

const isOnMaintenancePage = () => {
	return window.location.pathname.includes("temporarily-unavailable");
};

const isAuthRefreshLoopDetected = () => {
	return sessionStorage.getItem(AUTH_REFRESH_LOOP_KEY) === "1" || isOnMaintenancePage();
};

const goToMaintenance = () => {
	sessionStorage.setItem(AUTH_REFRESH_LOOP_KEY, "1");
	if (isOnMaintenancePage()) {
		return;
	}
	const lang = localStorage.getItem(LANGUAGE_KEY) || DEFAULT_LANGUAGE_KEY;
	window.location.replace(`/${lang}/temporarily-unavailable`);
};

const canTryRefresh = () => {
	if (isAuthRefreshLoopDetected()) {
		return false;
	}
	const now = Date.now();
	if (!refreshWindowStartedAt || now - refreshWindowStartedAt > REFRESH_WINDOW_MS) {
		refreshWindowStartedAt = now;
		refreshAttemptCount = 0;
	}
	refreshAttemptCount += 1;
	return refreshAttemptCount <= MAX_REFRESH_ATTEMPTS;
};

const api = axios.create({
	baseURL: process.env.REACT_APP_API_URL_KEY || "",
	withCredentials: true,
});

const dashboardApi = axios.create({
	baseURL: process.env.REACT_APP_DASHBOARD_API_URL_KEY || "",
	withCredentials: true,
});

const financeApi = axios.create({
	baseURL: process.env.REACT_APP_FINANCE_API_URL_KEY || "",
	withCredentials: true,
});

const cryptoApi = axios.create({
	baseURL: process.env.REACT_APP_CRYPTO_API_URL_KEY || "",
	withCredentials: true,
});

const identityApi = axios.create({
	baseURL: process.env.REACT_APP_IDENTITY_API_URL_KEY || "",
});

api.interceptors.request.use(async config => {
	config.headers = {
		Authorization: localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : "",
		language: localStorage.getItem("language") ? localStorage.getItem("language") : "en",
		'ngrok-skip-browser-warning': true,
	};
	return config;
});

dashboardApi.interceptors.request.use(async config => {
	config.headers = {
		Authorization: localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : "",
		language: localStorage.getItem("language") ? localStorage.getItem("language") : "en",
		'ngrok-skip-browser-warning': true,
	};
	return config;
});

financeApi.interceptors.request.use(async config => {
	config.headers = {
		Authorization: localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : "",
		language: localStorage.getItem("language") ? localStorage.getItem("language") : "en",
		'ngrok-skip-browser-warning': true,
	};
	return config;
});

cryptoApi.interceptors.request.use(async config => {
	config.headers = {
		Authorization: localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : "",
		language: localStorage.getItem("language") ? localStorage.getItem("language") : "en",
		'ngrok-skip-browser-warning': true,
	};
	return config;
});

const retryOriginalRequest = (originalRequest, newToken) => {
	originalRequest._retry = true;
	originalRequest.headers = originalRequest.headers || {};
	originalRequest.headers.Authorization = `Bearer ${newToken}`;
	return axios(originalRequest)
		.then(response => {
			sessionStorage.removeItem(AUTH_REFRESH_LOOP_KEY);
			resetRefreshAttempts();
			return response.data;
		})
		.catch(err => {
			if (err?.response?.status === 401 && !canTryRefresh()) {
				goToMaintenance();
			}
			return Promise.reject(err);
		});
};

api.interceptors.response.use(response => {
	sessionStorage.removeItem(AUTH_REFRESH_LOOP_KEY);
	resetRefreshAttempts();
	if (response.data) return response.data;
}, error => handleError(error));

dashboardApi.interceptors.response.use(response => {
	sessionStorage.removeItem(AUTH_REFRESH_LOOP_KEY);
	resetRefreshAttempts();
	if (response.data) return response.data;
}, error => handleError(error));

financeApi.interceptors.response.use(response => {
	sessionStorage.removeItem(AUTH_REFRESH_LOOP_KEY);
	resetRefreshAttempts();
	if (response.data) return response.data;
}, error => handleError(error));

cryptoApi.interceptors.response.use(response => {
	sessionStorage.removeItem(AUTH_REFRESH_LOOP_KEY);
	resetRefreshAttempts();
	if (response.data) return response.data;
}, error => handleError(error));

const handleError = async (error) => {
	if (error.response) {
		const originalRequest = error.config;
		if (error.response.status === 401 && originalRequest && !originalRequest._retry) {
			if (isAuthRefreshLoopDetected()) {
				return Promise.reject(error);
			}

			const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
			const user = localStorage.getItem(USER_KEY);
			const token = localStorage.getItem(TOKEN_KEY);
			if (!refreshToken || !user || !token) {
				logout();
				return Promise.reject(error);
			}

			// If a refresh is already in progress, queue this request
			// and retry it once the new token is available.
			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				}).then(newToken => retryOriginalRequest(originalRequest, newToken));
			}

			if (!canTryRefresh()) {
				processQueue(new Error("Too many refresh attempts"), null);
				goToMaintenance();
				return Promise.reject(error);
			}

			originalRequest._retry = true;
			isRefreshing = true;
			try {
				const newToken = await getUserDataByRefreshToken(refreshToken, user);
				processQueue(null, newToken);
				return retryOriginalRequest(originalRequest, newToken);
			} catch (err) {
				processQueue(err, null);
				logout();
				return Promise.reject(err);
			} finally {
				isRefreshing = false;
			}
		} else if (error.response.status === 401) {
			// Already retried after refresh, but API still returned 401.
			if (!canTryRefresh()) {
				goToMaintenance();
			}
			return Promise.reject(error);
		} else if (error.response.status === 404) { // Status Code: 404
			return Promise.reject("404 Not Found");
		} else if (error.response.status === 500) { // Status Code: 500
			return Promise.reject("500 Internal Server Error");
		}
		if (typeof error.response.data === OBJECT_KEY) {
			const currentError = { ...error.response.data };
			const errors = currentError.errors?.Error;
			let errorMessages = [];
			if (errors && errors.length > 0) {
				errors.forEach((error) => {
					const errorParams = error.params;
					const paramKeys = Object.keys(errorParams);
					paramKeys.forEach((key) => {
						const value = errorParams[key];
						if (typeof value === 'string' && value.trim().length > 0) {
							errorMessages.push(value);
						}
					});
				});
			} else {
				return Promise.reject("Unrecognized error");
			}
			if (errorMessages.length) {
				showError(errorMessages);
				return Promise.reject();
			} else {
				return Promise.reject("Unrecognized error");
			}
		} else {
			return Promise.reject("Unrecognized error");
		}

	} else {
		return Promise.reject("Unrecognized error");
	}
}

const showError = (errorMessages) => {
	const filteredStrings = errorMessages.filter(item => isNaN(item));
	filteredStrings.forEach(error => {
		AlertService.alert("error", error)
	});
}

const logout = () => {
	window.location.href = `/${localStorage.getItem(LANGUAGE_KEY)
		? localStorage.getItem(LANGUAGE_KEY)
		: DEFAULT_LANGUAGE_KEY
		}`;
	localStorage.removeItem("user");
	localStorage.removeItem("token");
	localStorage.removeItem("refreshToken");
};

const getUserDataByRefreshToken = (refreshToken, user) => {
	return new Promise((resolve, reject) => {
		ApiService.getUserDataByRefreshToken(refreshToken).then(response => {
			if (response && response.data && response.data.data && response.data.data.accessToken) {
				const data = { ...response.data.data };
				localStorage.setItem(TOKEN_KEY, data?.accessToken);
				localStorage.setItem(REFRESH_TOKEN_KEY, data?.refreshToken);
				localStorage.setItem(USER_KEY, user);
				resolve(data.accessToken);
			} else {
				reject(new Error("Invalid refresh token response"));
			}
		}).catch(error => {
			reject(error);
		});
	});
}

class ApiService {

	// RefreshToken
	static login(data) {
		return identityApi.post(`/account/Login`, data)
	};
	static getUserDataByRefreshToken(data) {
		return identityApi.get(`/account/refreshToken?refreshToken=${data}`);
	}

	static getAllTranslations() {
		return api.get(`/translation/GetByLanguageIsoCode`);
	};

	static getLanguages() {
		return api.get(`/language/getall`);
	};

	static getPagesAsAdmin() {
		return api.get(`/page/getAllAdmin`);
	};

	static getPageById(id) {
		return api.get(`/page/getAdmin/${id}`);
	};

	static createPage(data) {
		return api.post(`/page/create`, data);
	};

	static updatePage(data) {
		return api.put(`/page/update`, data);
	};

	static deletePage(pageId) {
		return api.delete(`/page/delete/${pageId}`);
	};

	static getPageContentById(contentId) {
		return api.get(`/page/GetContent/${contentId}`);
	};

	static createPageContent(data) {
		return api.post(`/page/createcontent`, data);
	};

	static updatePageContent(data) {
		return api.put(`/page/updatecontent`, data);
	};

	static getPageContents(pageId) {
		return api.get(`/page/GetContentsByPage/${pageId}`);
	};

	static deleteContent(contentId) {
		return api.delete(`/page/deletecontent/${contentId}`);
	};

	static onChangePageContentOrder(dropContentId, dragSectionId, languageId) {
		return api.put(`/page/ChangeContentOrder/${dropContentId}/${dragSectionId}/${languageId}`);
	};

	static sendError(data) {
		return api.post(`/error/create`, data);
	};

	static organizationUpdate(formData) {
		return api.put(`/organization/update`, formData);
	};

	static getReports() {
		return api.get(`/???`);
	};

	static getPaymentMethodsByCountryId(countryId) {
		return api.get(`/CountryPaymentMothodCurrency/GetPMethodByCountry/${countryId}`);
	};

	static getHolderTypesByCountry(countryId, paymentMethodId) {
		return api.get(`/CountryPaymentMothodCurrency/getHolderTypesByCountry?countryId=${countryId}&paymentMethodId=${paymentMethodId}`);
	};

	static getFiatCurrenciesByCountryIdAndPaymentMethodId(countryId, paymentMethodId) {
		return api.get(`/CountryPaymentMothodCurrency/GetByCountryAndPMethod?countryId=${countryId}&paymentMethodId=${paymentMethodId}`);
	};

	static getCountries() {
		// return api.get(`/country/GetBaseModels`);
		return api.get(`/country/GetAll`);
	};

	static generateKeys(data) {
		return api.post(`/organizationToken/create`, data);
	};

	static getAllKeys() {
		return api.get(`/organizationToken/getAllByOrganization`);
	};

	static checkPassword(password, publicKey) {
		return api.post(`/organizationToken/getKey?password=${password}&publicKey=${publicKey}`);
	};
	static getBanks(countryId, currencyId) {
		return api.get(`/Country/GetBankList?countryId=${countryId}&currencyId=${currencyId}`);
	};















	static currentUser() {
		return dashboardApi.get(`/user/current`);
	};

	static checkKey(key) {
		return dashboardApi.get(`/user/accessRestorePassword?key=${key}`);
	};

	static forgotPassword(email) {
		return dashboardApi.post(`/user/getRestorePasswordUrl?email=${email}`);
	};

	static restorePassword(data) {
		return dashboardApi.put(`/user/restorePassword`, data);
	};

	static changeOldPassword(data) {
		return dashboardApi.put(`/user/updatePassword`, data);
	};

	static deleteUserDocument(fileLibraryId, userId, type) {
		return dashboardApi.delete(`/user/DeleteDocument?libId=${fileLibraryId}&id=${userId}&type=${type}`);
	};

	static checkUserRegistration(key) {
		return dashboardApi.get(`/user/confirmEmail?emailVerificationKey=${key}`);
	};

	static uploadUserDocument(data) {
		return dashboardApi.post(`/user/UploadDocument`, data);
	};

	static updateProfile(data) {
		return dashboardApi.put(`/user/update`, data);
	};

	static updatePassword(data) {
		return dashboardApi.put(`/user/updatePassword`, data);
	};

	static createInstantPayment(data) {
		return dashboardApi.post(`/merchant/instantPayment/create`, data);
	};

	static getCryptoWallets() {
		return dashboardApi.get(`/cryptoWallet/list`);
	};

	static getCurrencies() {
		return dashboardApi.get(`/currency/cryptoList`);
	};

	static getFiatCurrencies() {
		return dashboardApi.get(`/currency/fiatList`);
	};

	static getKycLevels() {
		return dashboardApi.get(`/admin/Kyc/GetLevels`);
	};

	// Agent

	static getOrganizationTransactions(orgId, pageNumber, pageSize, paymentType) {
		return dashboardApi.get(`/merchant/transaction/getByOrg?orgId=${orgId}&pageNumber=${pageNumber}&pageSize=${pageSize}&paymentType=${paymentType}`);
	};

	static getOrganizationTransactionsAsAgent(pageNumber, pageSize, data, orgId) {
		return dashboardApi.post(`/agent/Payout/GetAll?orgId=${orgId}&pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};

	static getOrganizationTransactionsAsAdmin(orgId, pageNumber, pageSize, data) {
		return dashboardApi.post(`/admin/Transaction/getall?orgId=${orgId}&pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};



	// petqa arandzin endpoint vory kta agenti mijocov katarvac transactionnery (ham admini ham agenti hamar)
	static getAllTransactions(pageNumber, pageSize, filter) {
		return dashboardApi.post(`/admin/transaction/getall?pageNumber=${pageNumber}&pageSize=${pageSize}`, filter);
	};
	static getTransactionHistoryAsAgent(pageNumber, pageSize, filter) {
		return dashboardApi.post(`/admin/transaction/getall?pageNumber=${pageNumber}&pageSize=${pageSize}`, filter);
	};




	static getAllTransactionsAsPayingCustomer(pageNumber, pageSize, filter) {
		return dashboardApi.post(`/????pageNumber=${pageNumber}&pageSize=${pageSize}`, filter);
	};

	// agent dashboard
	static getOrganizationsAsAgent(userId = null, data) {
		return dashboardApi.post(`/agent/OrganizationAgent/GetOrganizations/${userId}`, data);
	};
	static getOrganizationsAsAdmin(userId, data) {
		return dashboardApi.post(`/admin/OrganizationAgent/GetOrganizations/${userId}`, data);
	};

	static getOrgBankAccounts(orgtId) {
		return dashboardApi.get(`/agent/bankAccount/GetOrgBankAccounts/${orgtId}`);
	};

	static getOrgBankAccountAsAgent(orgtId) {
		return dashboardApi.get(`/agent/bankAccount/GetOrgBankAccount/${orgtId}`);
	};

	static getOrgBankAccountAsAdmin(orgtId) {
		return dashboardApi.get(`/admin/bankAccount/GetOrgBankAccount/${orgtId}`);
	};

	static getOrgKycInformation(agentId, orgId) {
		return dashboardApi.get(`/agent/OrganizationAgent/GetByOrganizationId?agentId=${agentId}&orgId=${orgId}`);
	};

	static removeClient(clientId, agentId) {
		return dashboardApi.delete(`/agent/OrganizationAgent/Delete/${clientId}/${agentId}`);
	};

	static getClientTypes() {
		return dashboardApi.get(`/agent/AgentOrganizationInvitation/CreateView`);
	};

	static createClientInvitation(data) {
		return dashboardApi.post(`/agent/AgentOrganizationInvitation/Create`, data);
	};

	static getClientInvitationById(id) {
		return dashboardApi.post(`/agent/AgentOrganizationInvitation/Get/${id}`);
	};

	static deleteInvitation(id) {
		return dashboardApi.delete(`/agent/AgentOrganizationInvitation/Delete/${id}`);
	};

	static getClientInvitations() {
		return dashboardApi.get(`/agent/AgentOrganizationInvitation/GetAll`);
	};

	static getAgentProfileAsAgent(userId) {
		return dashboardApi.get(`/agent/Agent/GetProfile/${userId}`);
	};

	static getAgentInvitations(agentId, data) {
		return dashboardApi.post(`/admin/AgentOrganizationInvitation/GetListByAgent/${agentId}`, data);
	};
	static getAgentProfileAsAdmin(userId) {
		return dashboardApi.get(`/admin/Agent/GetProfile/${userId}`);
	};

	// end agent dashboard

	static getTransactionHistoryAsMerchant(pageNumber, pageSize, data,) {
		return dashboardApi.post(`/merchant/transaction/get?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};

	static getInstantPaymentsAsMerchant(pageNumber, pageSize, data) {
		return dashboardApi.post(`/merchant/InstantPayment/GetInstant?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};

	static getInstantPaymentsAsAdmin(pageNumber, pageSize, data) {
		return dashboardApi.post(`/admin/Transaction/GetAllInstants?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};

	static onFilterTransactionsAsMerchant(pageNumber, pageSize, data) {
		return dashboardApi.post(`/merchant/transaction/filter?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};

	static getTransactionByLocalTransactionId(localTransactionId) {
		return dashboardApi.get(`/merchant/transaction/details/${localTransactionId}`);
	};

	static getPaymentSystems() {
		return dashboardApi.get(`/merchant/payoutSystem/getall`);
	};

	// static getPayoutHistory(pageNumber, pageSize) {
	//   return dashboardApi.post(`/merchant/payout/getall?pageNumber=${pageNumber}&pageSize=${pageSize}`, {});
	// };

	static getPayoutHistoryAsAdmin(pageNumber, pageSize, data, orgId) {
		if (orgId) {
			return dashboardApi.post(`/admin/Payout/GetAll?pageNumber=${pageNumber}&pageSize=${pageSize}&orgId=${orgId}`, data);
		} else {
			return dashboardApi.post(`/admin/Payout/GetAll?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
		}
	};

	static getPayoutHistoryAsMerchant(pageNumber, pageSize, data, orgId) {
		if (orgId) {
			return dashboardApi.post(`/merchant/Payout/GetAll?pageNumber=${pageNumber}&pageSize=${pageSize}&orgId=${orgId}`, data);
		} else {
			return dashboardApi.post(`/merchant/Payout/GetAll?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
		}
	};

	static getPayoutByIdAsMerchant(itemId) {
		return dashboardApi.get(`/merchant/payout/get/${itemId}`);
	};

	static getPayoutByIdAsAdmin(itemId) {
		return dashboardApi.get(`/admin/payout/get/${itemId}`);
	};

	static onPayOut(data) {
		return dashboardApi.post(`/merchant/payout/create`, data);
	};

	static getNetValue(data) {
		return dashboardApi.post(`/merchant/payout/getNetValue`, data);
	};

	static getQueryFx(typeId) {
		return dashboardApi.get(`/merchant/BankAccount/QueryFx/${typeId}`);
	};

	static getAccounts() {
		return dashboardApi.get(`/merchant/bankAccount/getActiveAccounts`);
	};

	static getBankAccountAsMerchant() {
		return dashboardApi.get(`/merchant/bankAccount/getActiveAccount`);
	};

	static getBankAccountAsAgent() {
		return dashboardApi.get(`/agent/bankAccount/getActiveAccount`);
	};

	static getAccountByType(type) {
		return dashboardApi.get(`/merchant/bankAccount/getByType/${type}`);
	};

	static createAccountAsMerchant(values) {
		return dashboardApi.post(`/merchant/bankAccount/create`, values);
	};

	static createAccountAsAgent(values) {
		return dashboardApi.post(`/agent/bankAccount/create`, values);
	};

	static getAccountAsMerchant(id) {
		if (id) return dashboardApi.get(`/merchant/bankAccount/get/${id}`);
		else return dashboardApi.get(`/merchant/bankAccount/createView`);
	};

	static getAccountAsAgent(id) {
		if (id) return dashboardApi.get(`/agent/bankAccount/get/${id}`);
		else return dashboardApi.get(`/agent/bankAccount/createView`);
	};

	static updateOrganizationBankAccountAsMerchant(values) {
		return dashboardApi.put(`/merchant/bankAccount/update`, values);
	};

	static updateOrganizationBankAccountAsAgent(values) {
		return dashboardApi.put(`/agent/bankAccount/update`, values);
	};

	static updateOrganizationBankAccountAsAdmin(values) {
		return dashboardApi.put(`/admin/bankAccount/update`, values);
	};

	static filterPayouts(values, pageNumber, pageSize) {
		return dashboardApi.post(`/merchant/payout/filter?pageNumber=${pageNumber}&pageSize=${pageSize}`, values);
	};

	static getOrganizationData() {
		return dashboardApi.get(`/merchant/OrganizationDetail/get`);
	};
	static getAgentKycData() {
		return dashboardApi.get(`/agent/AgentKyc/Get`);
	};

	static getCustomers() {
		return dashboardApi.get(`/merchant/customer/get`);
	};

	static getProducts() {
		return dashboardApi.get(`/merchant/product/get`);
	};

	static getDashboardData() {
		return dashboardApi.get(`/merchant/dashboard/index`);
	};

	static deleteAccountAsMerchant(bankAccountId) {
		return dashboardApi.delete(`/merchant/bankAccount/delete/${bankAccountId}`);
	};

	static deleteAccountAsAgent(bankAccountId) {
		return dashboardApi.delete(`/agent/bankAccount/delete/${bankAccountId}`);
	};

	static getUsersAsMerchant(orgId, type, pageNumber, pageSize) {
		return dashboardApi.get(`/merchant/SignificantController/GetByOrg?orgId=${orgId}&type=${type}&pageNumber=${pageNumber}&pageSize=${pageSize}`);
	};

	static getUsersAsAgent(agentId, type, pageNumber, pageSize) {
		return dashboardApi.get(`/agent/SignificantController/GetByAgent?agentId=${agentId}&type=${type}&pageNumber=${pageNumber}&pageSize=${pageSize}`);
	};

	static getUserByIdAsMerchant(id) {
		return dashboardApi.get(`/merchant/SignificantController/Get/${id}`);
	};

	static getUserByIdAsAgent(id) {
		return dashboardApi.get(`/agent/SignificantController/Get/${id}`);
	};

	static getUserByIdAsAdmin(id) {
		return dashboardApi.get(`/admin/SignificantController/Get/${id}`);
	};

	static createUserAsMerchant(data) {
		return dashboardApi.post(`/merchant/SignificantController/Create`, data);
	};
	static createUserAsAgent(data) {
		return dashboardApi.post(`/agent/SignificantController/Create`, data);
	};

	static updateUserAsMerchant(data) {
		return dashboardApi.put(`/merchant/SignificantController/Update`, data);
	};

	static updateUserAsAgent(data) {
		return dashboardApi.put(`/agent/SignificantController/Update`, data);
	};

	static updateUserAsAdmin(data) {
		return dashboardApi.put(`/admin/SignificantController/Update`, data);
	};

	static getUserTypesAsMerchant() {
		return dashboardApi.get(`/merchant/SignificantController/GetTypes`);
	};

	static getUserTypesAsAdmin() {
		return dashboardApi.get(`/admin/SignificantController/GetTypes`);
	};

	static getUserTypesAsAgent() {
		return dashboardApi.get(`/agent/SignificantController/GetTypes`);
	};

	static deleteSignificantControllerDocumentAsMerchant(fileLibraryId, userId, type) {
		return dashboardApi.delete(`/merchant/SignificantController/DeleteDocument?libId=${fileLibraryId}&id=${userId}&type=${type}`);
	};

	static deleteSignificantControllerDocumentAsAdmin(fileLibraryId, userId, type) {
		return dashboardApi.delete(`/admin/SignificantController/DeleteDocument?libId=${fileLibraryId}&id=${userId}&type=${type}`);
	};

	static deleteSignificantControllerDocumentAsAgent(fileLibraryId, userId, type) {
		return dashboardApi.delete(`/agent/SignificantController/DeleteDocument?libId=${fileLibraryId}&id=${userId}&type=${type}`);
	};

	static deleteBankAccountDocumentAsMerchant(fileLibraryId, userId, type) {
		return dashboardApi.delete(`/merchant/bankAccount/DeleteDocument?libId=${fileLibraryId}&id=${userId}&type=${type}`);
	};

	static deleteBankAccountDocumentAsAgent(fileLibraryId, userId, type) {
		return dashboardApi.delete(`/agent/bankAccount/DeleteDocument?libId=${fileLibraryId}&id=${userId}&type=${type}`);
	};

	static deleteBankAccountDocumentAsAdmin(fileLibraryId, userId, type) {
		return dashboardApi.delete(`/admin/bankAccount/DeleteDocument?libId=${fileLibraryId}&id=${userId}&type=${type}`);
	};

	static uploadSignificantControllerDocumentAsMerchant(data) {
		return dashboardApi.post(`/merchant/SignificantController/UploadDocument`, data);
	};

	static uploadSignificantControllerDocumentAsAdmin(data) {
		return dashboardApi.post(`/admin/SignificantController/UploadDocument`, data);
	};

	static uploadSignificantControllerDocumentAsAgent(data) {
		return dashboardApi.post(`/agent/SignificantController/UploadDocument`, data);
	};

	static uploadBankAccountDocumentAsMerchant(data) {
		return dashboardApi.post(`/merchant/bankAccount/UploadDocument`, data);
	};

	static uploadBankAccountDocumentAsAgent(data) {
		return dashboardApi.post(`/agent/bankAccount/UploadDocument`, data);
	};

	static uploadBankAccountDocumentAsAdmin(data) {
		return dashboardApi.post(`/admin/bankAccount/UploadDocument`, data);
	};

	static deleteSignificantControllerAsMerchant(id) {
		return dashboardApi.delete(`/merchant/SignificantController/Delete/${id}`);
	};

	static deleteSignificantControllerAsAgent(id) {
		return dashboardApi.delete(`/agent/SignificantController/Delete/${id}`);
	};

	static getOrganization() {
		return dashboardApi.get(`/merchant/Organization/Get`);
	};

	static uploadOrganizationLogo(data) {
		return dashboardApi.post(`/merchant/Organization/UploadLogo`, data);
	};

	static deleteOrganizationLogo(libraryId) {
		return dashboardApi.delete(`/merchant/Organization/DeleteLogo/${libraryId}`);
	};

	static updateOrganization(data) {
		return dashboardApi.put(`/merchant/Organization/Update`, data);
	};

	static updateBusinessDetail(data) {
		return dashboardApi.put(`/merchant/OrganizationDetail/Update`, data);
	};

	static getOrganizationKYC(data) {
		return dashboardApi.post(`/merchant/OrganizationKyc/GetByOrgId`, data);
	};

	static getAgentViewOrgKYCAsAgent(orgId) {
		return dashboardApi.get(`/agent/OrganizationKyc/GetOrgKYC/${orgId}`);
	};

	static getOrgBankAccountAsAgent(orgId) {
		return dashboardApi.get(`/agent/OrganizationKyc/GetOrgBankAccount/${orgId}`);
	};

	static getAgentViewOrgKYCAsAdmin(data) {
		return dashboardApi.post(`/admin/OrganizationKyc/GetOrgKYCAgentView`, data);
	};

	static getAgentViewOrgKYCAsMerchant(data) {
		return dashboardApi.post(`/merchant/OrganizationKyc/GetOrgKYCAgentView`, data);
	};

	static createOrganizationKYC(data) {
		return dashboardApi.post(`/merchant/OrganizationKyc/Create`, data);
	};

	static uploadOrganizationKYCFile(data) {
		return dashboardApi.post(`/merchant/OrganizationKyc/UploadDocument`, data);
	};

	static deleteOrganizationKYCFile(data) {
		return dashboardApi.post(`/merchant/OrganizationKyc/DeleteDocument`, data);
	};

	static getOrganizationsByAdmin(pageNumber, pageSize, data) {
		return dashboardApi.post(`/admin/Organization/GetAll?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};

	static getOrganizationById(organizationId) {
		return dashboardApi.get(`/admin/Organization/GetById/${organizationId}`);
	};

	static getAgentKycDataById(agentId) {
		return dashboardApi.get(`/admin/agent/getKyc/${agentId}`);
	};

	static approveAgentInvitation(id) {
		return dashboardApi.put(`/admin/AgentOrganizationInvitation/UpdateStatus/${id}`);
	};

	static deleteAgentInvitation(id) {
		return dashboardApi.delete(`/agent/AgentOrganizationInvitation/Delete/${id}`);
	};

	static organizationDetailUpdateStatus(data) {
		return dashboardApi.put(`/merchant/OrganizationDetail/UpdateStatus`, data);
	};

	static organizationUpdateStatusAsMerchant(data) {
		return dashboardApi.put(`/merchant/Organization/UpdateStatus`, data);
	};
	static organizationUpdateStatusAsAgent(data) {
		return dashboardApi.put(`/agent/Agent/UpdateStatus`, data);
	};
	static checkMerchantAccount() {
		return dashboardApi.get(`/merchant/OrganizationRegistrationStatus/CheckStatus`);
	};

	//Files 
	static uploadDocumentAsMerchant(data) {
		return dashboardApi.post(`/merchant/OrganizationDetail/UploadDocument`, data);
	};
	static bulkUploadDocumentAsMerchant(data) {
		return dashboardApi.post(`/merchant/OrganizationDetail/BulkUploadDocument`, data);
	};
	static deleteDocumentAsMerchant(fileLibraryId, id, type) {
		return dashboardApi.delete(`/merchant/OrganizationDetail/DeleteDocument?libId=${fileLibraryId}&id=${id}&type=${type}`);
	};

	static uploadDocumentAsAdmin(data) {
		return dashboardApi.post(`/admin/OrganizationDetail/UploadDocument`, data);
	};
	static bulkUploadDocumentAsAdmin(data) {
		return dashboardApi.post(`/admin/OrganizationDetail/BulkUploadDocument`, data);
	};
	static deleteDocumentAsAdmin(fileLibraryId, id, type) {
		return dashboardApi.delete(`/admin/OrganizationDetail/DeleteDocument?libId=${fileLibraryId}&id=${id}&type=${type}`);
	};
	static approveOrganizationKYCDocument(data) {
		return dashboardApi.post(`/admin/OrganizationDetail/ApproveKycProof`, data);
	};
	static approveBankAccountDocument(data) {
		return dashboardApi.post(`/admin/BankAccount/ApproveKycProof`, data);
	};
	static uploadUserDocumentAsAdmin(data) {
		return dashboardApi.post(`/admin/user/UploadDocument`, data);
	};
	static deleteUserDocumentAsAdmin(fileLibraryId, id, type) {
		return dashboardApi.delete(`/admin/user/DeleteDocument?libId=${fileLibraryId}&id=${id}&type=${type}`);
	};
	static uploadLevelOneDocumentAsAdmin(data) {
		return dashboardApi.post(`/admin/User/UploadDocument`, data);
	};
	static deleteLevelOneDocumentAsAdmin(fileLibraryId, id, type) {
		return dashboardApi.delete(`/admin/User/DeleteDocument?libId=${fileLibraryId}&id=${id}&type=${type}`);
	};

	static updateOrganizationDetailOneLevel(data) {
		return dashboardApi.put(`/admin/OrganizationDetail/UpdateLevelOne`, data);
	};

	static updateOrganizationDetailTwoLevel(data) {
		return dashboardApi.put(`/admin/OrganizationDetail/UpdateLevelTwo`, data);
	};
	static updateOrganizationDetailLivenessTestStatus(data) {
		return dashboardApi.put(`/admin/OrganizationDetail/UpdateLivenessTestStatus`, data);
	};
	static updateOrganizationStatus(data) {
		return dashboardApi.put(`/admin/Organization/UpdateStatus`, data);
	};

	static updateAgentDataOneLevel(data) {
		return dashboardApi.put(`/admin/AgentKyc/UpdateLevelOne`, data);
	};
	static updateAgentDataTwoLevel(data) {
		return dashboardApi.put(`/admin/AgentKyc/UpdateLevelTwo`, data);
	};
	static updateAgentDataThreeLevel(data) {
		return dashboardApi.put(`/admin/bankAccount/update`, data);
	};

	static updateAgentStatus(data) {
		return dashboardApi.put(`/???`, data);
	};

	static updateAgentKyc(data) {
		return dashboardApi.put(`/agent/AgentKyc/Update`, data);
	};

	static disabledOrEnableOrganizationStatus(data) {
		return dashboardApi.put(`/admin/Organization/UpdateDisableStatus`, data);
	};

	static getAgents(pageNumber, pageSize, data) {
		return dashboardApi.post(`/admin/Agent/GetAll?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};

	static getAgentsListAsAdmin() {
		return dashboardApi.get(`/admin/Agent/GetList`);
	};


	static uploadDocumentAsAgent(data) {
		return dashboardApi.post(`/agent/AgentKyc/UploadDocument`, data);
	};
	static updateAgentProfile(data) {
		return dashboardApi.put(`/agent/Agent/Update`, data);
	};
	static bulkUploadDocumentAsAgent(data) {
		return dashboardApi.post(`/agent/AgentKyc/BulkUploadDocument`, data);
	};
	static deleteDocumentAsAgent(fileLibraryId, id, type) {
		return dashboardApi.delete(`/agent/AgentKyc/DeleteDocument?libId=${fileLibraryId}&id=${id}&type=${type}`);
	};
	static deleteDocumentAsPayingCustomer(fileLibraryId, id, type) {
		return dashboardApi.delete(`/?????libId=${fileLibraryId}&id=${id}&type=${type}`);
	};
	static updateAgentDataLivenessTestStatus(data) {
		return dashboardApi.put(`/admin/AgentKyc/UpdateLivenessTestStatus`, data);
	};
	static bulkUploadAgentKycDocumentAsAdmin(data) {
		return dashboardApi.post(`/admin/AgentKyc/BulkUploadDocument`, data);
	};
	static uploadAgentKycDocumentAsAdmin(data) {
		return dashboardApi.post(`/admin/AgentKyc/UploadDocument`, data);
	};
	static deleteAgentKycDocumentAsAdmin(fileLibraryId, userId, type) {
		return dashboardApi.delete(`/admin/AgentKyc/DeleteDocument?libId=${fileLibraryId}&id=${userId}&type=${type}`);
	};
	static updateAdminKycDataStatus(data) {
		return dashboardApi.put(`/admin/agent/UpdateStatus`, data);
	};
	static disabledOrEnableAdminKycStatus(data) {
		return dashboardApi.put(`/admin/agent/UpdateDisableStatus`, data);
	};
	static getAgentOrganizations(agentId, data) {
		return dashboardApi.post(`/admin/OrganizationAgent/GetOrganizations/${agentId}`, data);
	};
	static updateAgentKycLevel(data) {
		return dashboardApi.put(`/admin/Agent/UpdateLevel`, data);
	};
	static updateOrganizationKycLevel(data) {
		return dashboardApi.put(`/admin/Organization/UpdateLevel`, data);
	};
	static updateBankAccountStatusAsAdmin(data) {
		return dashboardApi.put(`/admin/bankaccount/updateStatus`, data);
	};
	static getOrganizationAttachedAgentId(organizationId) {
		return dashboardApi.get(`/admin/OrganizationAgent/GetByOrganizationId/${organizationId}`);
	};
	static attachOrganizationToAgentAsAdmin(data) {
		return dashboardApi.put(`/admin/OrganizationAgent/Attache`, data);
	};
	static detachAgentFromOrganizationAsAdmin(organizationId, agentId) {
		return dashboardApi.delete(`/admin/OrganizationAgent/Delete?orgId=${organizationId}&agentId=${agentId}`);
	};
	static getPaymentCustomersAsAdmin(pageNumber, pageSize, data) {
		return dashboardApi.post(`/admin/PaymentCustomer/GetAll?pageNumber=${pageNumber}&pageSize=${pageSize}`, data);
	};
	static approvePayOut(data) {
		return dashboardApi.post(`/admin/Payout/Accept`, data);
	};
	static approveInstantPayment(data) {
		return dashboardApi.post(`/???`, data);
	};
	static rejectInstantPayment(data) {
		return dashboardApi.post(`/???`, data);
	};
	static declinePayOut(data) {
		return dashboardApi.post(`/admin/Payout/Decline`, data);
	};
	static getTransactionOrganizers(transactionId) {
		return dashboardApi.get(`/admin/Transaction/GetOrganizers/${transactionId}`);
	};
	static getPayingCustomer(transactionId) {
		return dashboardApi.get(`/admin/PaymentCustomer/GetByTransactionId?transactionId=${transactionId}`);
	};
	static getPayingCustomerAsMerchant(transactionId) {
		return dashboardApi.get(`/merchant/PaymentCustomer/GetByTransactionId?transactionId=${transactionId}`);
	};
	static getTransactionOverview(transactionId) {
		return dashboardApi.get(`/admin/Transaction/Overview/${transactionId}`);
	};
	static getTransactionDetailsAsMerchant(transactionId) {
		return dashboardApi.get(`/merchant/Transaction/Details/${transactionId}`);
	};
	static getTransactionDetails(transactionId) {
		return dashboardApi.get(`/admin/Transaction/GetDetails/${transactionId}`);
	};
	static getBalance() {
		return dashboardApi.get(`/merchant/balance/get`);
	};
	static createPromotion(data) {
		return dashboardApi.post(`/Promotion/Create`, data);
	};
	static updatePromotion(data) {
		return dashboardApi.put(`/Promotion/update`, data);
	};
	static getPromotionData(id) {
		return dashboardApi.get(`/Promotion/Get/${id}`);
	};
	static getPromotionTypes() {
		return dashboardApi.get(`/Promotion/GetTypes`);
	};
	static getAllPromotions() {
		return dashboardApi.get(`/Promotion/GetAll`);
	};
	static deletePromotion(id) {
		return dashboardApi.delete(`/Promotion/Delete/${id}`);
	};
	static getShownPromotions(entityId, type) {
		return dashboardApi.get(`/EntityPromotion/Get?entityId=${entityId}&type=${type}`);
	};
	static updatePromotionStatus(id) {
		return dashboardApi.put(`/EntityPromotion/UpdateStatus?id=${id}`);
	};
	static getNotifications(data, type) {
		return dashboardApi.post(`/Notification/GetPagedList?receiverType=${type}`, data);
	};
	static updateNotificationStatus(id) {
		return dashboardApi.put(`/Notification/MakeRead?id=${id}`);
	};
	static getConfigByName(name) {
		return dashboardApi.get(`/Config/GetByName/${name}`);
	};
	static onChangeInstantPaymentStatus(data) {
		return dashboardApi.put(`/admin/Transaction/UpdateStatus?status=${data.status}&id=${data.id}`);
	};
	static anserTicket(data) {
		return dashboardApi.post(`/Support/Answer`, data);
	};
	static sendContactUsData(data) {
		return dashboardApi.post(`/Support/Post`, data);
	};
	static getTickets(pageNumber, pageSize) {
		return dashboardApi.post(`/Support/All?pageNumber=${pageNumber}&pageSize=${pageSize}`, {});
	};
	static getTicketById(id) {
		return dashboardApi.get(`/Support/get?id=${id}`);
	};
	static updateTicketStatus(id, ticketStatus) {
		return dashboardApi.get(`/Support/SetStatus?id=${id}&ticketStatus=${ticketStatus}`);
	};
	static getAccountTypes() {
		return dashboardApi.get(`/library/get?key=merchant_account_no_type`);
	};
	static getRoutingTypesLibrary() {
		return dashboardApi.get(`/library/get?key=routing_type`);
	};














	static getBankList(data) {
		return financeApi.post(`/v1/payment/getBankList`, data);
	};
	static getPayeeTemplate(data) {
		return financeApi.post(`/v1/Payee/Template`, data);
		// return financeApi.post(`/v1/PyvioTemplate/Filter`, data);
	};
	static getRoutingTypes(data) {
		return financeApi.post(`/v1/PyvioTemplate/GetRoutingTypes`, data);
	};




	static getOrganizationWallets(id) {
		return cryptoApi.post(`/wallet/getAllWalletsForMerchant?merchantId=${id}`);
	};
	static getWalletBalance(id) {
		return cryptoApi.get(`/wallet/getWalletBalance?walletId=${id}`);
	};


}

export default ApiService;
